import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { EmailDataSource } from './emails.data-source';
import { SmbEmailPreviewDialogComponent } from './smb-email-preview/smb-email-preview.component';
import { MatDialog } from '@angular/material/dialog';
import { EmailMessage, EmailService } from '@vendasta/email';
import { SiteService } from '../../core/site';
import { IsPartner, UserService } from '../../core/user.service';
import { PartnerEmailPreviewComponent } from './partner-email-preview/partner-email-preview.component';
import { provideGalaxyDateAdapter } from '@vendasta/galaxy/datepicker';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { FilterControl, FilterGroup } from '@vendasta/va-filter2';
import { ProductAnalyticsService } from '@vendasta/product-analytics';

@Component({
  selector: 'app-emails',
  templateUrl: './emails.component.html',
  styleUrls: ['./emails.component.scss'],
  providers: [provideGalaxyDateAdapter()],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class EmailsComponent implements OnInit, OnDestroy {
  emailDataSource: EmailDataSource;
  displayedColumns: string[] = ['timestamp', 'subject', 'to', 'from'];
  subscriptions: Subscription[] = [];
  isPartner = false;
  noEmailsImageUrl: Observable<string>;
  filterGroup: FilterGroup;

  config = [
    {
      canAddAndRemove: false,
    },
  ];

  constructor(
    private emailService: EmailService,
    private siteService: SiteService,
    private userService: UserService,
    public dialog: MatDialog,
    private translateService: TranslateService,
    public readonly analyticsService: ProductAnalyticsService,
    private snackBarService: SnackbarService,
  ) {
    this.filterGroup = new FilterGroup('emailFilters', [
      new FilterControl('to'),
      new FilterControl('from'),
      new FilterControl('sentAfter'),
      new FilterControl('sentBefore'),
    ]);
  }

  ngOnInit(): void {
    this.analyticsService.trackEvent('email:email-history', 'email-history', 'visit');
    this.emailDataSource = new EmailDataSource(
      this.emailService,
      this.siteService,
      this.analyticsService,
      this.snackBarService,
      this.translateService,
    );
    this.subscriptions.push(
      this.filterGroup.valueChanges.subscribe((fields) => this.emailDataSource.updateFilter(fields)),
      this.userService.isPartner$.subscribe((isPartner: IsPartner) => (this.isPartner = isPartner === IsPartner.Yes)),
    );
    this.updateNoLogsImage();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  /**
   * Previews the given email message using the EmailPreviewDialogComponent
   * @param email - EmailMessage object containing the email information to render
   */
  previewEmail(email: EmailMessage): void {
    if (this.isPartner) {
      this.dialog.open(PartnerEmailPreviewComponent, {
        data: email,
        panelClass: 'mat-dialog-no-padding',
      });
    } else {
      this.dialog.open(SmbEmailPreviewDialogComponent, {
        data: email,
        panelClass: 'mat-dialog-no-padding',
      });
    }
  }

  private updateNoLogsImage(): void {
    this.noEmailsImageUrl = this.translateService
      .stream('PAGES.EMAILS.NO_EMAILS.IMG')
      .pipe(map((url) => `https://vstatic-prod.apigateway.co/wsp-portal-client.9223372035065798524.demo/assets/images/${url}`));
  }
}
